import React, { memo, useCallback, useMemo } from "react";

// internal deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { isEmpty } from "@swiggy-private/js-utils";
import { useEffectOnce } from "@swiggy-private/core-ui";

// constants
import { DINE_CASH_POOLING_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";

// styles
import { QRCard, QRHeader, QRImageWrapper, TitleText, WhatsAppShareButton } from "./styles";

// constants
import { QR_CODE_ALT_TEXT, DINE_CASH_POOLING_TITLE, DINE_CASH_WHATSAPP_BUTTON_TEXT } from "../../constants";
import { ERROR_QR_CODE, QR_IMAGE_SIZE } from "./constants";

// helpers
import { generateQRCodeImageUrl } from "../../helpers";

interface IQRSectionProps {
    qrCodeImageUrl: string;
    onWhatsAppShare: VoidFunction;
    onQrCodeImpression?: VoidFunction;
    onWhatsAppShareImpression?: (text: string) => void;
    onWhatsAppShareClickEvent?: (text: string) => void;
    maxAmountReached?: boolean;
}

const QRWidgetSection = ({
    qrCodeImageUrl,
    onWhatsAppShare,
    onQrCodeImpression,
    onWhatsAppShareImpression,
    onWhatsAppShareClickEvent,
    maxAmountReached,
}: IQRSectionProps) => {
    useEffectOnce(() => {
        if (maxAmountReached) return;
        onQrCodeImpression?.();
        onWhatsAppShareImpression?.(DINE_CASH_WHATSAPP_BUTTON_TEXT);
    });
    const qrCodeImageUrlToDisplay = useMemo(
        () => (isEmpty(qrCodeImageUrl) ? ERROR_QR_CODE : generateQRCodeImageUrl(qrCodeImageUrl)),
        [qrCodeImageUrl],
    );

    const handleWhatsAppShare = useCallback(() => {
        onWhatsAppShareClickEvent?.(DINE_CASH_WHATSAPP_BUTTON_TEXT);
        onWhatsAppShare();
    }, [onWhatsAppShare, onWhatsAppShareClickEvent]);

    if (maxAmountReached) return null;

    return (
        <QRCard data-testid={DINE_CASH_POOLING_TEST_IDS.QR_CARD}>
            <QRHeader>
                <TitleText
                    variant="V2.overline.neutral.bold_spaced"
                    data-testid={DINE_CASH_POOLING_TEST_IDS.TITLE}
                    color="text_Highest_Emphasis"
                    textAlign="center"
                >
                    {DINE_CASH_POOLING_TITLE}
                </TitleText>
            </QRHeader>
            <QRImageWrapper>
                <Image
                    src={qrCodeImageUrlToDisplay}
                    alt={QR_CODE_ALT_TEXT}
                    width={QR_IMAGE_SIZE}
                    height={QR_IMAGE_SIZE}
                    fit="contain"
                    data-testid={DINE_CASH_POOLING_TEST_IDS.QR_IMAGE}
                />
            </QRImageWrapper>
            <WhatsAppShareButton
                onClick={handleWhatsAppShare}
                data-testid={DINE_CASH_POOLING_TEST_IDS.WHATSAPP_SHARE_BUTTON}
                buttonType="secondary"
                buttonSize="small"
                rightIconName="androidShare16"
            >
                {DINE_CASH_WHATSAPP_BUTTON_TEXT}
            </WhatsAppShareButton>
        </QRCard>
    );
};

export default memo(QRWidgetSection);
